import React from "react";

/**
 * VerqoLockup — official brand lockup (ship 2B · Check-V Hybrid).
 *
 *  Glyph: bold V whose right arm extends decisively past the top corner as a
 *  checkmark tail. Reads as both "V" (wordmark letter-link) AND "✓" (verqo
 *  literally means verified + go). ~14% canvas-fill stroke weight to match
 *  Brex / Linear logomark confidence.
 *
 *  Wordmark hierarchy: icon at full brand-fg, "verqo" softened to slate-700
 *  so the mark leads the lockup.
 *
 *  Sizes: "xs" (20px), "sm" (24px), "md" (32px · default), "lg" (40px), "xl" (48px)
 */

export const BRAND_FG = "#0c1612";
export const BRAND_ACCENT = "#dcff3a";

const SIZE_MAP = {
  xs: { box: 20, glyph: 14, radius: 4, word: 12, gap: 6 },
  sm: { box: 24, glyph: 17, radius: 5, word: 13, gap: 8 },
  md: { box: 32, glyph: 22, radius: 6, word: 16, gap: 10 },
  lg: { box: 40, glyph: 28, radius: 8, word: 20, gap: 12 },
  xl: { box: 48, glyph: 34, radius: 10, word: 24, gap: 14 },
};

export function CheckVGlyph({ size = 22, color = BRAND_ACCENT, stroke = null }) {
  // Stroke auto-tuned to ~14% of viewBox for optical consistency across sizes.
  const s = stroke ?? Math.max(3.6, (size / 32) * 4.4);
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M7 11 L15 23 L27 4"
        stroke={color}
        strokeWidth={s}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function VerqoLockup({
  size = "md",
  showWordmark = true,
  wordmarkColor,               // default derived from `dark` prop
  boxColor,                    // default derived from `dark` prop
  glyphColor,                  // default derived from `dark` prop
  dark = false,                // dark-bg variant (inverts mark chrome)
  className = "",
  testid = "verqo-lockup",
}) {
  const cfg = SIZE_MAP[size] || SIZE_MAP.md;
  // Dark-bg variant: lime box background against dark green bg with a dark
  // glyph — keeps the check-V readable when the page bg matches BRAND_FG.
  const resolvedBox   = boxColor   ?? (dark ? BRAND_ACCENT : BRAND_FG);
  const resolvedGlyph = glyphColor ?? (dark ? BRAND_FG      : BRAND_ACCENT);
  const resolvedWord  = wordmarkColor ?? (dark ? "#ffffff"  : "#334155");
  return (
    <div
      className={`flex items-center ${className}`}
      style={{ gap: cfg.gap }}
      data-testid={testid}
    >
      <div
        className="grid place-items-center flex-shrink-0"
        style={{
          width: cfg.box,
          height: cfg.box,
          background: resolvedBox,
          borderRadius: cfg.radius,
        }}
      >
        <CheckVGlyph size={cfg.glyph} color={resolvedGlyph} />
      </div>
      {showWordmark && (
        <span
          className="font-semibold tracking-tight leading-none"
          style={{ color: resolvedWord, fontSize: cfg.word }}
        >
          verqo
        </span>
      )}
    </div>
  );
}

export default VerqoLockup;
